import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ArrowRight, ChevronDown, ClipboardCheck, Scale, ShieldCheck } from 'lucide-react';
import LandingHeader from '@/components/landing/LandingHeader';
import LandingFooter from '@/components/landing/LandingFooter';
import { CASE_TYPE_DISCLAIMER, findCaseTypePage } from '@/data/caseTypePages';
import { PAGE_META } from '@/data/pageMeta';
import usePageMeta from '@/hooks/usePageMeta';
import shared from '@/styles/public.module.css';
import styles from './CaseTypePage.module.css';

export default function CaseTypePage({ slug }) {
  const navigate = useNavigate();
  const page = findCaseTypePage(slug);

  usePageMeta(PAGE_META[`caseType_${slug}`]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Defensive: a route can only exist for a real slug, so this is unreachable
  // in practice. It avoids a blank crash if the two ever drift apart.
  if (!page) return null;

  const applyPath = `/apply?type=${page.caseTypeValue}`;

  return (
    <div data-testid={`case-type-page-${page.slug}`}>
      <LandingHeader />

      <main>

      {/* Hero */}
      <section className={shared.section} data-testid="case-type-hero">
        <div className={shared.wrap}>
          <div className={styles.hero}>
            <span className={styles.badge}>{page.badge}</span>
            <h1 className={styles.title}>{page.h1}</h1>
            {page.intro.map((paragraph, index) => (
              <p key={index} className={styles.intro}>
                {paragraph}
              </p>
            ))}
            <div className={styles.heroActions}>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnAction}`}
                onClick={() => navigate(applyPath)}
                data-testid="case-type-apply-btn"
              >
                Apply for Funding
                <ArrowRight size={18} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={`${shared.btn} ${shared.btnQuiet}`}
                onClick={() => navigate('/faq')}
                data-testid="case-type-faq-btn"
              >
                Read the FAQ
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What you'll need — the case-specific section */}
      <section
        className={`${shared.section} ${shared.sectionPanel}`}
        data-testid="case-type-what-you-need"
      >
        <div className={shared.wrap}>
          <div className={styles.iconHead}>
            <div className={styles.iconBox}>
              <ClipboardCheck size={20} aria-hidden="true" />
            </div>
            <h2>{page.whatYouNeed.heading}</h2>
          </div>
          <div className={styles.cards}>
            {page.whatYouNeed.items.map((item) => (
              <div className={styles.card} key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className={shared.section} data-testid="case-type-how-it-works">
        <div className={shared.wrap}>
          <div className={`${shared.secHead} ${styles.centreHead}`}>
            <span className={shared.eyebrow}>How Funding Works</span>
            <h2>Three Steps</h2>
          </div>
          <div className={styles.steps}>
            {page.howItWorks.map((step, index) => (
              <div className={`${styles.card} ${styles.step}`} key={step.title}>
                <div className={styles.stepNum}>{index + 1}</div>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case-specific FAQs */}
      <section
        className={`${shared.section} ${shared.sectionPanel}`}
        data-testid="case-type-faqs"
      >
        <div className={shared.wrap}>
          <div className={styles.narrow}>
            <div className={styles.iconHead}>
              <div className={styles.iconBox}>
                <ShieldCheck size={20} aria-hidden="true" />
              </div>
              <h2>Questions About This Kind of Claim</h2>
            </div>

            <AccordionPrimitive.Root type="multiple" defaultValue={[`${page.slug}-faq-0`]}>
              {page.faqs.map((faq, index) => (
                <AccordionPrimitive.Item
                  key={faq.q}
                  value={`${page.slug}-faq-${index}`}
                  className={styles.faqItem}
                >
                  <AccordionPrimitive.Header>
                    <AccordionPrimitive.Trigger
                      className={styles.faqTrigger}
                      data-testid={`case-type-faq-trigger-${index}`}
                    >
                      <span>{faq.q}</span>
                      <ChevronDown size={20} className={styles.faqChevron} aria-hidden="true" />
                    </AccordionPrimitive.Trigger>
                  </AccordionPrimitive.Header>
                  <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                    <div className={styles.faqAnswer}>
                      {faq.body.map((paragraph, i) => (
                        <p key={i}>{paragraph}</p>
                      ))}
                    </div>
                  </AccordionPrimitive.Content>
                </AccordionPrimitive.Item>
              ))}
            </AccordionPrimitive.Root>

            <p className={styles.disclaimer} data-testid="case-type-disclaimer">
              {CASE_TYPE_DISCLAIMER}
            </p>
          </div>
        </div>
      </section>

      {/* Cost and non-recourse reassurance — this page does not set the price */}
      <section className={shared.section} data-testid="case-type-cost-reassurance">
        <div className={shared.wrap}>
          <div className={styles.centred}>
            <div className={styles.iconCentre}>
              <div className={styles.iconBox}>
                <Scale size={20} aria-hidden="true" />
              </div>
            </div>
            <h2>This Page Does Not Set the Price</h2>
            <p>
              Nothing on this page determines what an advance would cost you. Every advance
              made through MyCaseLoan is non-recourse, repaid only out of a successful
              settlement — never out of pocket, and never owed at all if your case does not
              succeed.
            </p>
            <p>
              The fees and the rate that would apply to your case come from the funder’s own
              offer, not from this page. That is exactly why comparing every offer you
              receive side by side, before you sign anything, is the whole point of using a
              marketplace instead of approaching a single funder directly.
            </p>
            <a
              href="/faq"
              className={styles.link}
              onClick={(event) => {
                event.preventDefault();
                navigate('/faq');
              }}
              data-testid="case-type-cost-faq-link"
            >
              See how the cost is actually calculated
              <ArrowRight size={15} aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section
        className={`${shared.section} ${shared.sectionDark}`}
        data-testid="case-type-final-cta"
      >
        <div className={shared.wrap}>
          <div className={styles.finalInner}>
            <h2>See What Funders Would Offer</h2>
            <p>
              Applying takes a few minutes, costs nothing, and puts your verified claim in front
              of funders who compete for it. You repay only if your case succeeds.
            </p>
            <button
              type="button"
              className={`${shared.btn} ${shared.btnAccent}`}
              onClick={() => navigate(applyPath)}
              data-testid="case-type-cta-apply-btn"
            >
              Apply for Funding
              <ArrowRight size={18} aria-hidden="true" />
            </button>
          </div>
        </div>
      </section>
      </main>


      <LandingFooter />
    </div>
  );
}
